import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { cs, enUS } from 'date-fns/locale'

import { toast } from '../../components/LsToast'
import { ScreenWrapper } from '../../components/ScreenWrapper'
import { Path } from '../../router/BaseRouter/enums'
import { getCustomers } from '../../service/customers.service'
import { CustomerSearchResultDto } from '../../service/types'
import { drawerMarginSx } from '../../utils/drawerMargin'
import { formatLastLesson } from '../../utils/formatLastLesson'
import { CustomerDialog } from './CustomerDialog'
import { CustomerLessonsDrawer, DRAWER_WIDTH } from './CustomerLessonsDrawer'
import { filterCustomers } from './filterCustomers'

export const Customers: FC = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { customerId } = useParams<{ customerId?: string }>()
  const [customers, setCustomers] = useState<CustomerSearchResultDto[]>([])
  const [loading, setLoading] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerSearchResultDto | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const loadCustomers = useCallback(async () => {
    setLoading(true)
    try {
      const response = await getCustomers()
      setCustomers(response.data)
    } catch {
      toast.error(t('general.notification.unexpectedError'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    void loadCustomers()
  }, [loadCustomers])

  const dateLocale = i18n.language === 'cs' ? cs : enUS

  const filteredCustomers = useMemo(() => {
    const filtered = filterCustomers(customers, searchQuery)
    return [...filtered].sort((a, b) => {
      const dateCompare = new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      if (dateCompare !== 0) return dateCompare
      return a.name.localeCompare(b.name)
    })
  }, [customers, searchQuery])

  const drawerCustomer = useMemo(() => customers.find((c) => c.id === customerId) ?? null, [customers, customerId])
  const drawerOpen = drawerCustomer !== null

  const handleAdd = () => {
    setSelectedCustomer(null)
    setDialogOpen(true)
  }

  const handleEdit = (customer: CustomerSearchResultDto) => {
    if (customerId) navigate(Path.CUSTOMERS, { replace: true })
    setSelectedCustomer(customer)
    setDialogOpen(true)
  }

  const handleRowClick = (customer: CustomerSearchResultDto) => {
    if (customerId === customer.id) {
      navigate(Path.CUSTOMERS)
    } else {
      navigate(`${Path.CUSTOMERS}/${customer.id}`)
    }
  }

  return (
    <>
      <ScreenWrapper
        title={t('customers.title')}
        sx={drawerMarginSx(drawerOpen, DRAWER_WIDTH)}
        filter={
          <Box display="flex" alignItems="center" gap={2}>
            <TextField
              size="small"
              placeholder={t('customers.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ backgroundColor: 'background.paper' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
              {t('customers.addCustomer')}
            </Button>
          </Box>
        }
      >
        {loading && <LinearProgress sx={{ mt: 2 }} />}
        <TableContainer component={Paper} sx={{ mt: loading ? 0 : 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('customers.form.name')}</TableCell>
                <TableCell>{t('customers.form.phone')}</TableCell>
                <TableCell>{t('customers.form.email')}</TableCell>
                <TableCell>{t('customers.lastLesson')}</TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && filteredCustomers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary">
                      {t('customers.noResults')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {filteredCustomers.map((customer) => (
                <TableRow key={customer.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleRowClick(customer)}>
                  <TableCell>{customer.name}</TableCell>
                  <TableCell>{customer.phone ?? '\u2014'}</TableCell>
                  <TableCell>{customer.email ?? '\u2014'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatLastLesson(customer.lastLessonDate, t, dateLocale)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        navigate(Path.CUSTOMER_DETAIL.replace(':customerId', customer.id))
                      }}
                    >
                      <OpenInNewIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleEdit(customer)
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <CustomerDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSaved={() => void loadCustomers()}
          customer={selectedCustomer}
        />
      </ScreenWrapper>

      <CustomerLessonsDrawer open={drawerOpen} onClose={() => navigate(Path.CUSTOMERS)} customer={drawerCustomer} />
    </>
  )
}
